import currency from 'currency.js';
import { DateTime } from 'luxon';
import generatePaymentPeriodOptions from './generatePaymentPeriodOptions';
import {
  IBuyerReimbursementFromSellerViewItem,
  IBuyerReimbursementOtherFromSellerViewItem,
  IBuyerReimbursementOtherToSellerViewItem,
  IBuyerReimbursementToSellerViewItem,
  IEscrowAtClosingOtherViewItem,
  IEscrowAtClosingViewItem,
} from 'ui-toolkit/cdItems/interfaces/ICDViewItem';
import { ePaymentPeriod } from '../../../ui-toolkit/enums/ePaymentPeriod';
import { convertToFloat } from '../../../ui-toolkit/utilities/convertToFloat';

const hasValue = (value?: any | null) => value !== undefined && value !== null;
const paymentPeriodAsOptions = generatePaymentPeriodOptions();

type IAllBuyerReimbursementFromSellerPresentationItems =
  | IBuyerReimbursementFromSellerViewItem
  | IBuyerReimbursementOtherFromSellerViewItem
  | IBuyerReimbursementOtherToSellerViewItem
  | IBuyerReimbursementToSellerViewItem;
type IAllEscrowAtClosingPresentationItems = IEscrowAtClosingViewItem | IEscrowAtClosingOtherViewItem;
export const updateEscrowComputedFields = <
  T extends IAllEscrowAtClosingPresentationItems,
  TKey extends keyof IAllEscrowAtClosingPresentationItems,
>(
  data: T,
  key: TKey
): T => {
  const hasComputedDataFields = hasValue(data.monthly_payment) && hasValue(data.months);
  const monthlyPayment = convertToFloat(data.monthly_payment);
  const months = convertToFloat(data.months);
  return {
    ...data,
    [key]: hasComputedDataFields ? currency(monthlyPayment || 0).multiply(months || 0).value : null,
    dirty: true,
  };
};

export const updateBuyerReimbursementComputedFields = <T extends IAllBuyerReimbursementFromSellerPresentationItems>(
  data: T,
  key: 'buyer_at_closing' | 'seller_at_closing'
): T => {
  const hasComputedDataFields =
    hasValue(data.period_amount) &&
    hasValue(data.date_from) &&
    hasValue(data.date_to) &&
    paymentPeriodAsOptions.find((option) => option.value === data.billing_period);
  if (hasComputedDataFields) {
    const dateFrom = DateTime.fromJSDate(data.date_from!);
    const dateTo = DateTime.fromJSDate(data.date_to!);
    const diffDays = dateTo.diff(dateFrom, ['days']).days + 1;
    const amountPerPeriod = convertToFloat(data.period_amount);

    const paymentsPerPeriod = new Map();
    paymentsPerPeriod.set(ePaymentPeriod.annual, 1);
    paymentsPerPeriod.set(ePaymentPeriod.semi_annual, 2);
    paymentsPerPeriod.set(ePaymentPeriod.quarterly, 4);
    paymentsPerPeriod.set(ePaymentPeriod.monthly, 12);

    const numberOfPayments = paymentsPerPeriod.get(data.billing_period);
    const monthsPerPayment = 12 / numberOfPayments;
    const startMonth = dateFrom.month;
    const startYear = dateFrom.year;

    let startPeriodMonth;

    if (startMonth % monthsPerPayment > 0) {
      startPeriodMonth = Math.floor(startMonth / monthsPerPayment) * monthsPerPayment + 1;
    } else {
      startPeriodMonth = (startMonth / monthsPerPayment - 1) * monthsPerPayment + 1;
    }
    const endPeriodMonth = startPeriodMonth - 1 + monthsPerPayment;
    const startPeriodDate = DateTime.utc(startYear, startPeriodMonth, 1);
    const endPeriodDate = DateTime.utc(startYear, endPeriodMonth, 1).plus({ months: 1 }).minus({ days: 1 });
    const periodDays = endPeriodDate.diff(startPeriodDate, ['days']).days + 1;
    const amount = amountPerPeriod * (diffDays / periodDays);
    return {
      ...data,
      dirty: true,
      [key]: currency(amount || 0).value,
    };
  }
  return {
    ...data,
    [key]: null,
  };
};

export const calculatePeriodDays = <T extends IAllBuyerReimbursementFromSellerPresentationItems>(data: T): number => {
  const dateFrom = DateTime.fromJSDate(data.date_from!);

  const paymentsPerPeriod = new Map();
  paymentsPerPeriod.set(ePaymentPeriod.annual, 1);
  paymentsPerPeriod.set(ePaymentPeriod.semi_annual, 2);
  paymentsPerPeriod.set(ePaymentPeriod.quarterly, 4);
  paymentsPerPeriod.set(ePaymentPeriod.monthly, 12);

  const numberOfPayments = paymentsPerPeriod.get(data.billing_period);
  const monthsPerPayment = 12 / numberOfPayments;
  const startMonth = dateFrom.month;
  const startYear = dateFrom.year;

  let startPeriodMonth;

  if (startMonth % monthsPerPayment > 0) {
    startPeriodMonth = Math.floor(startMonth / monthsPerPayment) * monthsPerPayment + 1;
  } else {
    startPeriodMonth = (startMonth / monthsPerPayment - 1) * monthsPerPayment + 1;
  }
  const endPeriodMonth = startPeriodMonth - 1 + monthsPerPayment;
  const startPeriodDate = DateTime.utc(startYear, startPeriodMonth, 1);
  const endPeriodDate = DateTime.utc(startYear, endPeriodMonth, 1).plus({ months: 1 }).minus({ days: 1 });
  const periodDays = endPeriodDate.diff(startPeriodDate, ['days']).days + 1;
  return periodDays;
};

export const hintTextAmount = <T extends IAllBuyerReimbursementFromSellerPresentationItems>(data: T): string | null => {
  const hasComputedDataFields =
    hasValue(data.period_amount) &&
    hasValue(data.date_from) &&
    hasValue(data.date_to) &&
    paymentPeriodAsOptions.find((option) => option.value === data.billing_period);
  if (hasComputedDataFields) {
    const { date_to, date_from, period_amount } = data;
    const periodDays = calculatePeriodDays(data);
    const amountPerPeriod = convertToFloat(period_amount);

    const dateFrom = DateTime.fromJSDate(date_from!);
    const dateTo = DateTime.fromJSDate(date_to!);

    const diffDays = dateTo.diff(dateFrom, ['days']).days + 1;

    const amount = amountPerPeriod * (diffDays / periodDays);

    return `${diffDays}/${periodDays} * $${amountPerPeriod} = $${amount.toFixed(2)}`;
  }
  return '';
};

export const hintTextDays = <T extends IAllBuyerReimbursementFromSellerPresentationItems>(data: T): string | null => {
  const hasComputedDataFields =
    hasValue(data.period_amount) &&
    hasValue(data.date_from) &&
    hasValue(data.date_to) &&
    paymentPeriodAsOptions.find((option) => option.value === data.billing_period);
  if (hasComputedDataFields) {
    const { date_to, date_from } = data;

    const dateFrom = DateTime.fromJSDate(date_from!);
    const dateTo = DateTime.fromJSDate(date_to!);

    const diffDays = dateTo.diff(dateFrom, ['days']).days + 1;

    return `Number of days : ${diffDays}`;
  }
  return '';
};

export const hintTextPeriod = <T extends IAllBuyerReimbursementFromSellerPresentationItems>(data: T): string | null => {
  const hasComputedDataFields =
    hasValue(data.period_amount) &&
    hasValue(data.date_from) &&
    hasValue(data.date_to) &&
    paymentPeriodAsOptions.find((option) => option.value === data.billing_period);
  if (hasComputedDataFields) {
    const periodDays = calculatePeriodDays(data);
    return `Number of days in billing period : ${periodDays}`;
  }
  return '';
};
